/* eslint-disable no-console */
import { first, kebabCase, last } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AgendaTemplateHealthProfessionalApplyModal } from '../../compositions/agenda/modals/agenda-template-health-professional-apply-modal/agenda-template-health-professional-apply-modal';
import { ScheduleHealthProfessionalUpdateModal } from '../../compositions/schedule/modals/schedule-health-professional-update-modal/schedule-health-professional-update-modal';
import { SchedulePracticeWeekSchedule } from '../../compositions/schedule/schedule/schedule-practice-week-schedule';
import { useAppContext } from '../../data/context/app-context';
import { useHealthProfessionalItems } from '../../data/data/health-professionals/use-health-professional-items';
import { useScheduleItems } from '../../data/data/schedule/use-schedule-items';
import { PracticeId } from '../../data/data/types';
import {
  getScheduleFirstMondayOfWeek,
  getScheduleRangeForWeek,
} from '../../data/data/utils';
import { HealthProfessionalItemDataFragment } from '../../data/types/api/graphql';
import { useFormatters } from '../../data/utils/formatters.utils';
import { useNavigateWithHealthProvider } from '../../data/utils/router.utils';
import { Button, ButtonIcon, ButtonType } from '../../elements/button/button';
import { Content } from '../../elements/content/content';
import { ContentLoader } from '../../elements/content/content-loader';
import { ContentMessage } from '../../elements/content/content-message';
import { BaseInputIcon } from '../../elements/form/input/base-input';
import { InputField } from '../../elements/form/input/input-field';
import { InputFieldPager } from '../../elements/form/input-field-pager';
import { CustomFormattedMessage } from '../../elements/intl/custom-formatted-message';
import { Sidebar } from '../../elements/sidebar/sidebar';
import { SidebarList } from '../../elements/sidebar-list/sidebar-list';
import { SidebarListItem } from '../../elements/sidebar-list/sidebar-list-item';
import { Tooltip } from '../../elements/tooltip/tooltip';

export function SchedulePage() {
  const { healthProviderId, user } = useAppContext();

  const [editable, setEditable] = useState<boolean>(false);

  const [applyTemplateModalOpen, setApplyTemplateModalOpen] =
    useState<boolean>(false);

  const [updateScheduleModalOpen, setUpdateScheduleModalOpen] =
    useState<boolean>(false);

  // fix if not healthProfessionalId
  const { healthProfessionalId, year, weekNr } = useParams();
  const navigate = useNavigateWithHealthProvider();

  const nav = useNavigate();

  const location = useLocation();

  useEffect(() => {
    const segments: string[] = location.pathname
      .replace(/^\/|\/$/g, '')
      .split('/');

    console.log(segments);
    const scheduleIndex = segments.findIndex(segment => segment === 'schedule');

    if (scheduleIndex !== -1) {
      const scheduleSegments = segments.slice(scheduleIndex + 1);

      // The ISO week-year, not the calendar year: 1 January 2027 is in 2026-W53.
      const currentYear = new Date().getWeekYear().toString();
      const currentWeek = new Date().getWeek().toString();

      const w = scheduleSegments[scheduleSegments.length - 1] || currentWeek;
      const y = scheduleSegments[scheduleSegments.length - 2] || currentYear;
      const id = scheduleSegments[scheduleSegments.length - 3] || PracticeId;

      console.log(`NAVIGATE : Schedule id ${id} / year ${y} / week ${w}`);
      nav(`/${healthProviderId}/schedule/${id}/${y}/${w}`, {
        replace: true,
      });

      console.log(`id ${id} / year ${y} / week ${w}`);
    } else {
      console.log('No schedule segment found in path');
    }
  }, [healthProviderId, location.pathname, nav]);

  const isPractice = useMemo(
    () => healthProfessionalId === PracticeId,
    [healthProfessionalId]
  );

  const {
    data: healthProfessionalItemsData,
    loading: healthProfessionalItemsLoading,
  } = useHealthProfessionalItems({ healthProviderId, onlyAgenda: true });

  const healthProfessionalItems = useMemo(
    () => healthProfessionalItemsData?.items ?? [],
    [healthProfessionalItemsData?.items]
  );

  const date = useMemo(
    () => getScheduleRangeForWeek(weekNr, year),
    [weekNr, year]
  );

  const { data: scheduleItemsData, loading: scheduleItemsLoading } =
    useScheduleItems({
      healthProviderId,
      healthProfessionalId,
      startDate: date.firstDay,
      endDate: date.lastDay,
      defaultFirst: false,
    });

  const onHandleHealthProfessional = useCallback(
    (option: HealthProfessionalItemDataFragment | string) => {
      navigate(
        `/schedule/${typeof option === 'string' ? option : option.id}/${
          date.year
        }/${date.weekNr}`
      );
    },
    [date, navigate]
  );

  const onHandleCurrentWeek = useCallback(() => {
    const currentYear = new Date().getWeekYear().toString();
    const currentWeek = new Date().getWeek().toString();

    // Use the same format as in the useEffect
    nav(
      `/${healthProviderId}/schedule/${healthProfessionalId}/${currentYear}/${currentWeek}`
    );
  }, [healthProfessionalId, healthProviderId, nav]);

  const onHandleWeek = useCallback(
    (currentWeek: string) => {
      navigate(
        `/schedule/${healthProfessionalId}/${first(
          currentWeek.split('-')
        )!}/${last(currentWeek.split('-'))!.replace('W', '')}`
      );
    },
    [healthProfessionalId, navigate]
  );

  const onHandleWeekPager = useCallback(
    (type: 'prev' | 'next') => {
      const firstMondayOfWeek = getScheduleFirstMondayOfWeek(
        date.weekNr,
        date.year
      )!;

      if (type === 'prev') {
        firstMondayOfWeek.setDate(firstMondayOfWeek.getDate() - 7);
      } else if (type === 'next') {
        firstMondayOfWeek.setDate(firstMondayOfWeek.getDate() + 7);
      }

      const currentWeek = firstMondayOfWeek.getWeekWithYear();

      navigate(
        `/schedule/${healthProfessionalId}/${first(
          currentWeek.split('-')
        )!}/${last(currentWeek.split('-'))!.replace('W', '')}`
      );
    },
    [healthProfessionalId, navigate, date]
  );

  const onHandleEditable = useCallback(() => {
    setEditable(prevState => !prevState);
  }, []);

  const { getFullName } = useFormatters();

  return (
    <>
      <Sidebar
        title={<CustomFormattedMessage id="schedules.title" />}
        toolbar={
          user?.isAdmin === true ? (
            <Button
              icon={ButtonIcon.LIST_ADD}
              onClick={() => setApplyTemplateModalOpen(true)}
            >
              <CustomFormattedMessage id="agenda.button.apply-template" />
            </Button>
          ) : undefined
        }
      >
        <SidebarList
          loading={healthProfessionalItemsLoading}
          items={[
            ...[
              <SidebarListItem
                key={PracticeId}
                title={<CustomFormattedMessage id="common.schedule-practice" />}
                active={healthProfessionalId === PracticeId}
                onClick={() => onHandleHealthProfessional(PracticeId)}
              />,
            ],
            ...healthProfessionalItems.map(option => (
              <SidebarListItem
                key={option.id}
                title={
                  <>
                    {getFullName(option)}
                    <small>
                      <CustomFormattedMessage
                        id={`common.honorific.${kebabCase(option.honorific)}`}
                      />
                    </small>
                  </>
                }
                active={healthProfessionalId === option.id}
                onClick={() => onHandleHealthProfessional(option)}
              />
            )),
          ]}
        />
      </Sidebar>

      <Content
        toolbar={
          healthProfessionalId ? (
            <>
              <Button inverted onClick={onHandleCurrentWeek}>
                <CustomFormattedMessage id="common.current-week" />
              </Button>

              <InputFieldPager
                onClickPrev={() => onHandleWeekPager('prev')}
                onClickNext={() => onHandleWeekPager('next')}
              >
                <InputField
                  type="week"
                  name="week"
                  icon={BaseInputIcon.DATE}
                  value={date.week}
                  onChange={onHandleWeek}
                />
              </InputFieldPager>
              {user?.isAdmin === true && (
                <>
                  <Tooltip
                    message={
                      <CustomFormattedMessage id="common.schedule-locked" />
                    }
                  >
                    <Button
                      type={ButtonType.SECONDARY}
                      icon={editable ? ButtonIcon.LOCK_OPEN : ButtonIcon.LOCK}
                      inverted
                      onClick={onHandleEditable}
                    >
                      {editable ? (
                        <CustomFormattedMessage id="common.unlocked" />
                      ) : (
                        <CustomFormattedMessage id="common.locked" />
                      )}
                    </Button>
                  </Tooltip>

                  <Button
                    icon={ButtonIcon.EDIT}
                    disabled={!healthProfessionalId || isPractice}
                    onClick={() => setUpdateScheduleModalOpen(true)}
                  >
                    <CustomFormattedMessage id="schedules.button.add-change" />
                  </Button>
                </>
              )}
            </>
          ) : undefined
        }
      >
        {!healthProfessionalId && (
          <ContentMessage
            message={<CustomFormattedMessage id="schedules.select-schedule" />}
          />
        )}

        {healthProfessionalId &&
          (scheduleItemsLoading ? (
            <ContentLoader />
          ) : (
            <SchedulePracticeWeekSchedule
              healthProviderId={healthProviderId}
              healthProfessionalId={healthProfessionalId}
              date={date}
              data={scheduleItemsData}
              editable={editable}
            />
          ))}
      </Content>

      {applyTemplateModalOpen && (
        <AgendaTemplateHealthProfessionalApplyModal
          healthProviderId={healthProviderId}
          healthProfessionalId={isPractice ? null : healthProfessionalId}
          onClose={() => {
            setApplyTemplateModalOpen(false);
          }}
        />
      )}

      {updateScheduleModalOpen && (
        <ScheduleHealthProfessionalUpdateModal
          healthProviderId={healthProviderId}
          healthProfessionalId={healthProfessionalId}
          onClose={() => {
            setUpdateScheduleModalOpen(false);
          }}
        />
      )}
    </>
  );
}
